import { Badge, Button, Card, CardContent } from '@estac1/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.js';

interface Topic {
  id: string;
  category: string;
  title: string;
  summary: string;
  example: string;
  screens: string | null;
  occurrences: number;
  status: 'open' | 'resolved' | 'dismissed';
  lastSeenAt: string;
}

const CATEGORY_LABEL: Record<string, string> = {
  knowledge_gap: 'falta no conhecimento',
  product_bug: 'possível bug',
  missing_feature: 'pedido de recurso',
  guard_false_positive: 'filtro barrou pergunta boa',
};

const STATUS_LABEL: Record<Topic['status'], string> = {
  open: 'aberto',
  resolved: 'resolvido',
  dismissed: 'descartado',
};

const day = (iso: string) => new Date(iso).toLocaleDateString('pt-BR', { dateStyle: 'short' });

/**
 * Temas da triagem diária (#686): o que os donos perguntaram e o assistente
 * não resolveu, agrupado por problema e sem dado pessoal. Resolvido que volta
 * a acontecer reabre sozinho.
 */
export const TopicList = () => {
  const qc = useQueryClient();
  const topics = useQuery({
    queryKey: ['admin-assistant-topics'],
    queryFn: () => api.request<{ topics: Topic[] }>('/v1/admin/assistant/topics'),
  });
  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: Topic['status'] }) =>
      api.request(`/v1/admin/assistant/topics/${id}`, { method: 'PATCH', json: { status } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-assistant-topics'] }),
  });
  const list = topics.data?.topics ?? [];
  if (topics.isLoading || list.length === 0) return null;

  return (
    <Card>
      <CardContent className="space-y-3 py-4">
        <h2 className="text-sm font-semibold">
          Temas da triagem ({list.filter((t) => t.status === 'open').length} abertos)
        </h2>
        <ul className="divide-y divide-border">
          {list.map((topic) => (
            <li key={topic.id} className="space-y-1 py-3 text-sm">
              <p className="flex flex-wrap items-center gap-2 text-xs text-muted-fg">
                <Badge variant={topic.status === 'open' ? 'warning' : 'default'}>
                  {STATUS_LABEL[topic.status]}
                </Badge>
                {CATEGORY_LABEL[topic.category] ?? topic.category} · {topic.occurrences} respostas ·
                última em {day(topic.lastSeenAt)}
                {topic.screens ? ` · ${topic.screens}` : ''}
              </p>
              <p className="font-medium">{topic.title}</p>
              <p className="text-muted-fg">{topic.summary}</p>
              <p className="text-xs text-muted-fg">Exemplo: {topic.example}</p>
              {topic.status === 'open' && (
                <div className="flex gap-2 pt-1">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={setStatus.isPending}
                    onClick={() => setStatus.mutate({ id: topic.id, status: 'resolved' })}
                  >
                    Resolvido
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={setStatus.isPending}
                    onClick={() => setStatus.mutate({ id: topic.id, status: 'dismissed' })}
                  >
                    Descartar
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};
